import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ArrowUpRight, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [plan, setPlan] = useState("");

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setPlan(data.plan || ""); setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) { /* keep polling */ }
      attempts += 1;
      if (attempts >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="relative flex min-h-screen items-center justify-center overflow-hidden navy-mesh px-6 py-16 text-white">
      <div className="pointer-events-none absolute -right-32 top-10 h-96 w-96 rounded-full bg-brand/25 blur-3xl" />
      <div className="relative w-full max-w-lg text-center">
        <div className="mb-10 flex justify-center"><Logo light /></div>

        {state === "checking" && (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-12 backdrop-blur">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-brand" />
            <h1 className="mt-6 font-display text-2xl font-bold">Confirming your payment…</h1>
            <p className="mt-2 text-slate-400">Hang tight, this only takes a moment.</p>
          </div>
        )}

        {state === "paid" && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="rounded-3xl border border-brand/30 bg-white/5 p-12 backdrop-blur">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15, type: "spring", stiffness: 200 }} className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-brand shadow-lg shadow-brand/40">
              <CheckCircle2 className="h-11 w-11 text-white" />
            </motion.div>
            <h1 data-testid="success-heading" className="mt-8 font-display text-3xl font-black tracking-tight sm:text-4xl">Congratulations on starting your Massive Growth journey!</h1>
            {plan && <p className="mt-3 text-brand font-semibold">{plan} activated</p>}
            <p className="mt-4 text-lg leading-relaxed text-slate-300">Your payment was successful. Our team will be reaching out to you shortly to kick things off.</p>
            <Link to="/" data-testid="success-home-link" className="mt-9 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand-hover">
              Back to home <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        )}

        {(state === "timeout" || state === "error") && (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-12 backdrop-blur">
            <XCircle className="mx-auto h-12 w-12 text-amber-400" />
            <h1 className="mt-6 font-display text-2xl font-bold">
              {state === "timeout" ? "Still processing…" : "We couldn't confirm your payment"}
            </h1>
            <p className="mt-3 text-slate-300">
              {state === "timeout"
                ? "Your payment may still be completing. If you were charged, our team will reach out shortly."
                : "No charge may have been made. Please try again or contact us and we'll help right away."}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/" className="rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white hover:border-brand">Back to home</Link>
              <Link to="/contact" className="rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand-hover">Contact us</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
